import { useState, useEffect, useRef, useCallback } from 'react';

/** Same delay as UserList / My Dashboards — avoids firing search on every keystroke. */
export const SEARCH_DEBOUNCE_MS = 500;

/**
 * Controlled search field + debounced term for API calls or filtering.
 * @param {number} [debounceMs]
 * @returns {{ searchInput: string, setSearchInput: React.Dispatch<React.SetStateAction<string>>, searchTerm: string, setSearchTerm: React.Dispatch<React.SetStateAction<string>>, clearSearch: () => void }}
 */
export function useDebouncedSearch(debounceMs = SEARCH_DEBOUNCE_MS) {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (searchInput !== searchTerm) setSearchTerm(searchInput);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchInput, searchTerm, debounceMs]);

  const clearSearch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSearchInput('');
    setSearchTerm('');
  }, []);

  return { searchInput, setSearchInput, searchTerm, setSearchTerm, clearSearch };
}
